// The installation settings page's view model (SCR-156): what one installation's layered
// settings say about the deployment: applied state per task, the fields its IaC pins, drift
// between a task's pins and the stored override, and the fields waiting on a redeploy. Pure and
// DOM-free (tests/unit/installation-settings-model.test.ts); fetching and rendering live in
// installation-settings.tsx.
import type { components } from "../../api/schema";
import { FIELDS, type FieldDescriptor } from "@nexus/ui/settings/validate";
import { type Json } from "@nexus/ui/settings/inherit";
import { appliedSummary, pinsFromDocument, type SettingsLayers } from "@nexus/ui/screens/models";
import type { ChipTone } from "@nexus/ui";

export type ReportRow = components["schemas"]["SettingsReportRow"];

/** An RFC 6901 pointer (`/models/codex/default`) as the form's dotted path (`models.codex.default`). */
export function pointerToPath(pointer: string): string {
  return pointer
    .split("/")
    .slice(1)
    .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"))
    .join(".");
}

/** The field a pointer (or an ancestor or descendant of it) names, for its label; null when none. */
export function fieldForPointer(pointer: string, fields: readonly FieldDescriptor[] = FIELDS): FieldDescriptor | null {
  const path = pointerToPath(pointer);
  const exact = fields.find((field) => field.path === path);
  if (exact) return exact;
  // The closest field above the pointer, else the first one below it.
  const above = fields.filter((field) => path.startsWith(`${field.path}.`)).sort((a, b) => b.path.length - a.path.length)[0];
  return above ?? fields.find((field) => field.path.startsWith(`${path}.`)) ?? null;
}

export interface PointerRow {
  pointer: string;
  label: string;
  /** The IaC parameter that pins the field, when the field has one. */
  parameter: string | null;
}

export function pointerRow(pointer: string, fields: readonly FieldDescriptor[] = FIELDS): PointerRow {
  const field = fieldForPointer(pointer, fields);
  return { pointer, label: field?.label || pointerToPath(pointer) || "Whole document", parameter: field?.iac ?? null };
}

export function pointerRows(pointers: readonly string[], fields: readonly FieldDescriptor[] = FIELDS): PointerRow[] {
  return pointers.map((pointer) => pointerRow(pointer, fields));
}

export interface PinRow {
  path: string;
  label: string;
  parameter: string;
}

/** Every pinned field with the parameter that pins it, in the schema's order. */
export function pinRows(pins: Json | undefined, fields: readonly FieldDescriptor[] = FIELDS): PinRow[] {
  const byPath = pinsFromDocument(pins, fields as FieldDescriptor[]);
  return fields.filter((field) => byPath[field.path] !== undefined).map((field) => ({ path: field.path, label: field.label || field.path, parameter: byPath[field.path]! }));
}

export type TaskState = "applied" | "behind" | "lkg_only";

export interface TaskRow {
  taskId: string;
  state: TaskState;
  stateLabel: string;
  tone: ChipTone;
  digest: string;
  pluginVersion: string;
  reportedAt: string;
  /** The last-known-good warning, when the task serves the sealed document. */
  warning: string | null;
}

const LKG_WARNING = "The settings API was unreachable, so this task runs its last known good document. Changes made since will reach it when it can pull again.";

/** `lkg_only` wins over the digest: a task on the sealed document is not confirmed as current. */
export function taskRow(row: ReportRow, effectiveDigest: string): TaskRow {
  const base = { taskId: row.taskId, digest: row.digest, pluginVersion: row.pluginVersion, reportedAt: row.reportedAt };
  if (row.mode === "lkg_only") return { ...base, state: "lkg_only", stateLabel: "Last known good only", tone: "amber", warning: LKG_WARNING };
  if (row.digest === effectiveDigest) return { ...base, state: "applied", stateLabel: "Applied", tone: "green", warning: null };
  return { ...base, state: "behind", stateLabel: "Not yet applied", tone: "amber", warning: null };
}

export function taskRows(layers: Pick<SettingsLayers, "reported" | "effectiveDigest">): TaskRow[] {
  return layers.reported.map((row) => taskRow(row, layers.effectiveDigest));
}

/** The newest report's time: when the settings were last confirmed on a task, or null with none. */
export function latestReportAt(reported: readonly ReportRow[]): string | null {
  let latest: string | null = null;
  for (const row of reported) if (latest === null || row.reportedAt > latest) latest = row.reportedAt;
  return latest;
}

export type Health = "no_report" | "applied" | "lkg_only" | "drift" | "behind" | "redeploy";

export interface InstallationSettingsView {
  health: Health;
  healthLabel: string;
  tone: ChipTone;
  summary: string;
  appliedAt: string | null;
  tasks: TaskRow[];
  pins: PinRow[];
  drift: PointerRow[];
  needsRedeploy: PointerRow[];
  overrideVersion: number;
  overrideUpdatedAt: string | null;
  overrideUpdatedBy: string | null;
}

/** Drift (a hand-set parameter disagreeing with the override) outranks the rest of the warnings. */
export function describeInstallation(layers: SettingsLayers, fields: readonly FieldDescriptor[] = FIELDS): InstallationSettingsView {
  const summary = appliedSummary(layers);
  const tasks = taskRows(layers);
  const drift = pointerRows(layers.drift, fields);
  const needsRedeploy = pointerRows(layers.needsRedeploy, fields);
  let health: Health;
  let healthLabel: string;
  let tone: ChipTone;
  if (summary.total === 0) [health, healthLabel, tone] = ["no_report", "No report yet", "grey"];
  else if (drift.length > 0) [health, healthLabel, tone] = ["drift", "Drift", "red"];
  else if (needsRedeploy.length > 0) [health, healthLabel, tone] = ["redeploy", "Needs redeploy", "amber"];
  else if (summary.lkgOnly > 0) [health, healthLabel, tone] = ["lkg_only", "Last known good only", "amber"];
  else if (layers.applied) [health, healthLabel, tone] = ["applied", "Applied", "green"];
  else [health, healthLabel, tone] = ["behind", "Not yet applied", "amber"];
  return {
    health,
    healthLabel,
    tone,
    summary: summary.text,
    appliedAt: layers.applied ? latestReportAt(layers.reported) : null,
    tasks,
    pins: pinRows(layers.pins as unknown as Json, fields),
    drift,
    needsRedeploy,
    overrideVersion: layers.override?.version ?? 0,
    overrideUpdatedAt: layers.override?.updatedAt ?? null,
    overrideUpdatedBy: layers.override?.updatedBy ?? null,
  };
}

export interface OverviewRow {
  id: string;
  name: string;
  view: InstallationSettingsView | null;
  /** A failed load, shown on the row instead of hiding it. */
  failed: boolean;
}

/** Installations needing attention first (drift, redeploy, lkg, behind), then the rest by name. */
const RANK: Record<Health, number> = { drift: 0, redeploy: 1, lkg_only: 2, behind: 3, no_report: 4, applied: 5 };

export function sortOverview(rows: readonly OverviewRow[]): OverviewRow[] {
  const rank = (row: OverviewRow) => (row.view ? RANK[row.view.health] : -1);
  return [...rows].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}
